import React from 'react';
import Snackbar from '@mui/material/Snackbar';
import MuiAlert, { AlertColor } from '@mui/material/Alert';
import Dialog from '@mui/material/Dialog';
import DialogTitle from '@mui/material/DialogTitle';
import DialogContent from '@mui/material/DialogContent';
import DialogActions from '@mui/material/DialogActions';
import Button from '@mui/material/Button';
import TextField from '@mui/material/TextField';
import IconButton from '@mui/material/IconButton';
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import Grid from '@mui/material/Grid';
import CloseIcon from '@mui/icons-material/Close';
import { useMutation } from '@apollo/client';
import { gql } from '@apollo/client';

const EDIT_SIM_MUTATION = gql`
mutation Mutation($simId:String!,$msisdn1:String!,$msisdn2:String!){
editSim(sim_id:$simId,msisdn1:$msisdn1,msisdn2:$msisdn2){
status
message
}
}
`;

interface EditSimDialogProps {
    open: boolean;
    onClose: () => void;
    device?: {
        manuf?: string;
        modelCode?: string;
        model?: string;
        imei?: string;
        serial?: string;
        simProvider?: string;
        iccid?: string;
        tsp1?: string;
        msisdn1?: string;
        tsp2?: string;
        msisdn2?: string;
        simStatus?: string;
        activatedOn?: string;
        validityMonths?: string;
        whiteListedIps?: string;
        whiteListedPhone?: string;
        [key: string]: any;
    };
    onSaveSuccess?: (data: any) => void;
}

const EditSimDialog: React.FC<EditSimDialogProps> = ({ open, onClose, device, onSaveSuccess }) => {
    const [formData, setFormData] = React.useState(device || {});
    const [editSimMutation, { loading, error }] = useMutation(EDIT_SIM_MUTATION);
    const [snackbar, setSnackbar] = React.useState<{ open: boolean; message: string; severity: AlertColor }>({ open: false, message: '', severity: 'success' });
    const hasChanges = (device?.msisdn1 !== formData.msisdn1) || (device?.msisdn2 !== formData.msisdn2);
    React.useEffect(() => {
        if (device) {
            setFormData(device);
        }
        setSnackbar({ open: false, message: '', severity: 'success' });
    }, [device]);

    const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        const { name, value } = e.target;
        setFormData((prev) => ({ ...prev, [name]: value.replace(/[^a-zA-Z0-9]/g, '')}));
    };

    const handleSnackbarClose = () => {
        setSnackbar(prev => ({ ...prev, open: false }));
    };

    const handleSubmit = async () => {
        if (!formData.msisdn1 || !formData.msisdn2) {
            setSnackbar({ open: true, message: 'MSISDN 1 and MSISDN 2 cannot be empty.', severity: 'warning' });
            return;
        }
        if (!hasChanges) {
            setSnackbar({ open: true, message: 'No changes were made.', severity: 'info' });
            return;
        }
        try {
            setSnackbar({ open: true, message: 'Updating SIM details...', severity: 'success' });
            const { data } = await editSimMutation({
                variables: {
                    simId: formData.sim_id || formData.iccid || '',
                    msisdn1: formData.msisdn1.trim() || '',
                    msisdn2: formData.msisdn2.trim() || ''
                }
            });
            setSnackbar({ open: true, message: 'SIM updated successfully!', severity: 'success' });
            if (data?.editSim?.status === true || data?.editSim?.message === 'SIM updated successfully!') {
                if (onSaveSuccess) {
                    onSaveSuccess({ ...formData });
                }
                  setSnackbar({ open: true, message: 'SIM updated successfully!', severity: 'success' });
                setTimeout(() => {
                    onClose();
                }, 2000);
            } else {
                setSnackbar({ open: true, message: data?.editSim?.message || 'Failed to update SIM', severity: 'error' });
                console.error('Failed to update SIM:', data?.editSim?.message);
            }
        } catch (err) {
            setSnackbar({ open: true, message: 'Error updating SIM', severity: 'error' });
            console.error('Error updating SIM:', err);
        }
    };

    return (
        <Dialog
            open={open}
            onClose={onClose}
            maxWidth="md"
            fullWidth
            sx={{
                '& .MuiDialog-paper': {
                    borderRadius: 2,
                    width: '100%',
                    maxWidth: '800px',
                }
            }}
        >
            <DialogTitle sx={{
                backgroundColor: '#ffffffff',
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
                padding: '16px 24px'
            }}>
                <Typography variant="h6" sx={{ fontWeight: 'bold' }}>
                    Edit SIM Details
                </Typography>
                <IconButton
                    aria-label="close"
                    onClick={onClose}
                    sx={{
                        color: '#000',
                        '&:hover': {
                            backgroundColor: 'rgba(0,0,0,0.1)'
                        }
                    }}
                >
                    <CloseIcon />
                </IconButton>
            </DialogTitle>

            <DialogContent  sx={{ padding: '24px' }}>
                {error && (
                    <Typography color="error" sx={{ mb: 2 }}>
                        Error:{error.message}
                    </Typography>
                )}
                <Grid container spacing={3}>
                    <Snackbar
                        open={snackbar.open}
                        autoHideDuration={3000}
                        onClose={handleSnackbarClose}
                        anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
                    >
                        <MuiAlert
                            onClose={handleSnackbarClose}
                            severity={snackbar.severity}
                            sx={{ width: '100%' }}
                            elevation={6}
                            variant="filled"
                        >
                            {snackbar.message}
                        </MuiAlert>
                    </Snackbar>
                    <Grid sx={{ xs: 12, md: 3 }}>
                        <TextField
                            fullWidth
                            margin="normal"
                            label="Device Manuf Code"
                            name="manuf"
                            value={formData.manuf || ''}
                            onChange={handleChange}
                            disabled
                        />
                    </Grid>

                    <Grid sx={{ xs: 12, md: 3 }}>
                        <TextField
                            fullWidth
                            margin="normal"
                            label="Device Model"
                            name="model"
                            value={formData.model || ''}
                            onChange={handleChange}
                            disabled
                        />
                    </Grid>
                    <Grid sx={{ xs: 12, md: 3 }}>
                        <TextField
                            fullWidth
                            margin="normal"
                            label="Device IMEI"
                            name="imei"
                            value={formData.imei || ''}
                            onChange={handleChange}
                            disabled
                        />
                    </Grid>
                    <Grid sx={{ xs: 12, md: 3 }}>
                        <TextField
                            fullWidth
                            margin="normal"
                            label="Device Serial"
                            name="serial"
                            value={formData.serial || ''}
                            onChange={handleChange}
                            disabled
                        />
                    </Grid>
                    <Grid sx={{ xs: 12, md: 3 }}>
                        <TextField
                            fullWidth
                            margin="normal"
                            label="SIM Provider"
                            name="simProvider"
                            value={formData.simProvider || ''}
                            onChange={handleChange}
                            disabled
                        />
                    </Grid>
                    <Grid sx={{ xs: 12, md: 3 }}>
                        <TextField
                            fullWidth
                            margin="normal"
                            label="ICCID"
                            name="iccid"
                            value={formData.iccid || ''}
                            onChange={handleChange}
                            disabled
                        />
                    </Grid>
                    <Grid sx={{ xs: 12, md: 3 }}>
                        <TextField
                            fullWidth
                            margin="normal"
                            label="TSP 1"
                            name="tsp1"
                            value={formData.tsp_1 || ''}
                            onChange={handleChange}
                            disabled
                        />
                    </Grid>
                    <Grid sx={{ xs: 12, md: 3 }}>
                        <TextField
                            fullWidth
                            margin="normal"
                            label="MSISDN 1"
                            name="msisdn1"
                            value={formData.msisdn1 || ''}
                            inputProps={{ maxLength: 20 }}
                            onChange={handleChange}
                        />
                    </Grid>
                    <Grid sx={{ xs: 12, md: 3 }}>
                        <TextField
                            fullWidth
                            margin="normal"
                            label="TSP 2"
                            name="tsp2"
                            value={formData.tsp_2 || ''}
                            onChange={handleChange}
                            disabled
                        />
                    </Grid>
                    <Grid sx={{ xs: 12, md: 3 }}>
                        <TextField
                            fullWidth
                            margin="normal"
                            label="MSISDN 2"
                            name="msisdn2"
                            value={formData.msisdn2 || ''}
                            inputProps={{ maxLength: 20 }}
                            onChange={handleChange}
                        />
                    </Grid>
                    <Grid sx={{ xs: 12, md: 3 }}>
                        <TextField
                            fullWidth
                            margin="normal"
                            label="SIM Status"
                            name="simStatus"
                            value={formData.simStatus || ''}
                            onChange={handleChange}
                            disabled
                        />
                    </Grid>
                </Grid>
            </DialogContent>

            <DialogActions sx={{
                padding: '16px 24px',
                justifyContent: 'flex-end'
            }}>
                <Button
                    onClick={onClose}
                    variant="outlined"
                  
                >
                    Cancel
                </Button>
                <Button
                    onClick={handleSubmit}
                    variant="contained"
                    disabled={loading || !hasChanges}
                    color="error"
                    
                >
                    {loading ? 'Saving...' : 'Save'}
                </Button>
            </DialogActions>
        </Dialog>
    );
};

export default EditSimDialog;